import React, { useEffect, useMemo, useState } from 'react';
import Jazzicon, { jsNumberForAddress } from 'react-jazzicon';
import { resolveAttachmentUrl } from '../../../utils/attachmentUrl';
import { copyToClipboard, shortenWalletAddress } from '../../../utils/commonFunction';
import { EMPLOYMENT_TYPE } from '../../../utils/profileFormMaps';

const PAGE_SIZE = 8;

const STATUS_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
];

function normalizeStatus(status) {
    return String(status || 'active').toLowerCase();
}

const TYPE_FILTERS = [
    { id: 'all', label: 'All types' },
    { id: EMPLOYMENT_TYPE.SALARIED, label: 'Salaried' },
    { id: EMPLOYMENT_TYPE.FREELANCE, label: 'Freelance' },
];

function normalizeEmploymentType(type) {
    return String(type || EMPLOYMENT_TYPE.FREELANCE).toLowerCase() === EMPLOYMENT_TYPE.SALARIED
        ? EMPLOYMENT_TYPE.SALARIED
        : EMPLOYMENT_TYPE.FREELANCE;
}

function formatCompensation(employee, tokenSymbol = 'USDC') {
    const type = normalizeEmploymentType(employee.employmentType);
    if (type === EMPLOYMENT_TYPE.SALARIED) {
        const salary = employee.monthlySalary;
        if (salary == null || salary === '') return '—';
        const num = Number(salary);
        if (!Number.isFinite(num)) return String(salary);
        return (
            <>
                <span className="font-semibold text-zinc-900 dark:text-white">
                    ${num.toFixed(2)}
                </span>
                <span className="ml-1 font-normal text-zinc-500 dark:text-zinc-500">
                    {tokenSymbol}/mo
                </span>
            </>
        );
    }
    return formatHourlyRate(employee.hourlyRate, tokenSymbol);
}

const EmploymentTypePill = ({ type }) => {
    const normalized = normalizeEmploymentType(type);
    const isSalaried = normalized === EMPLOYMENT_TYPE.SALARIED;
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                isSalaried
                    ? 'border-indigo-500/30 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300'
                    : 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300'
            }`}
        >
            {isSalaried ? 'Salaried' : 'Freelance'}
        </span>
    );
};

function formatHourlyRate(rate, symbol = 'USDC') {
    if (rate == null || rate === '') return '—';
    const num = Number(rate);
    if (!Number.isFinite(num)) return String(rate);
    return (
        <>
            <span className="font-semibold text-zinc-900 dark:text-white">${num.toFixed(2)}</span>
            <span className="ml-1 font-normal text-zinc-500 dark:text-zinc-500">{symbol}</span>
        </>
    );
}

const WalletAvatar = ({ address, imageUrl, size = 32 }) => {
    const src = imageUrl ? resolveAttachmentUrl(imageUrl) : '';
    return (
        <div
            className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-lg bg-zinc-100 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700"
            style={{ width: size, height: size }}
        >
            {src ? (
                <img src={src} alt="" className="h-full w-full object-cover" />
            ) : address ? (
                <Jazzicon diameter={size} seed={jsNumberForAddress(address)} />
            ) : (
                <i className="fa-solid fa-wallet text-[10px] text-zinc-400" />
            )}
        </div>
    );
};

const OverviewStatCard = ({ label, value, hint, tone = 'zinc', selected, onClick }) => {
    const tones = {
        emerald: {
            ring: selected ? 'ring-emerald-500/50' : 'ring-transparent',
            icon: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
            value: 'text-emerald-700 dark:text-emerald-300',
        },
        zinc: {
            ring: selected ? 'ring-zinc-400/50' : 'ring-transparent',
            icon: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
            value: 'text-zinc-900 dark:text-white',
        },
        amber: {
            ring: selected ? 'ring-amber-500/40' : 'ring-transparent',
            icon: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
            value: 'text-amber-800 dark:text-amber-300',
        },
        teal: {
            ring: selected ? 'ring-teal-500/50' : 'ring-transparent',
            icon: 'bg-teal-500/10 text-teal-700 dark:text-teal-400',
            value: 'text-teal-800 dark:text-teal-300',
        },
    };
    const t = tones[tone] || tones.zinc;

    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex w-full items-start gap-4 rounded-2xl border border-zinc-200 bg-white p-4 text-left shadow-sm ring-2 transition hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-950/50 dark:hover:border-zinc-700 ${t.ring}`}
        >
            <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${t.icon}`}
            >
                <i
                    className={`text-sm ${
                        tone === 'emerald'
                            ? 'fa-solid fa-user-check'
                            : tone === 'amber'
                              ? 'fa-solid fa-user-slash'
                              : tone === 'teal'
                                ? 'fa-solid fa-users'
                                : 'fa-regular fa-user'
                    }`}
                />
            </span>
            <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-500">
                    {label}
                </p>
                <p className={`mt-1 text-2xl font-bold tabular-nums ${t.value}`}>{value}</p>
                {hint ? (
                    <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-500">{hint}</p>
                ) : null}
            </div>
        </button>
    );
};

const StatusPill = ({ status }) => {
    const normalized = normalizeStatus(status);
    const isActive = normalized === 'active';
    const isRemoved = normalized === 'removed';

    if (isRemoved) {
        return (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/25 bg-rose-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                Removed
            </span>
        );
    }

    return (
        <span
            className={`relative inline-flex items-center gap-1.5 overflow-hidden rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                isActive
                    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                    : 'border-zinc-300 bg-zinc-100 text-zinc-600 dark:border-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-400'
            }`}
        >
            {isActive ? (
                <span className="absolute inset-y-0 left-0 w-0.5 bg-emerald-500" aria-hidden />
            ) : null}
            <span
                className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-zinc-400'}`}
            />
            {isActive ? 'Active' : 'Inactive'}
        </span>
    );
};

const EmployeesDirectory = ({
    employees = [],
    tokenSymbol = 'USDC',
    isCompanyOwner = false,
    canAddEmployee = false,
    submitting = false,
    statusUpdatingId = null,
    onAddEmployee,
    onViewEmployee,
    onEditEmployee,
    onToggleStatus,
    onFundPayroll,
    payrollFundingId = null,
    onRemoveEmployee,
}) => {
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);

    const roster = useMemo(
        () => employees.filter((e) => normalizeStatus(e.status) !== 'removed'),
        [employees],
    );

    const filtered = useMemo(() => {
        let list =
            statusFilter === 'all'
                ? roster
                : roster.filter((e) => normalizeStatus(e.status) === statusFilter);

        if (typeFilter !== 'all') {
            list = list.filter(
                (e) => normalizeEmploymentType(e.employmentType) === typeFilter,
            );
        }

        const query = searchQuery.trim().toLowerCase();
        if (!query) {
            return list;
        }

        return list.filter((employee) => {
            const name = String(employee.name || '').toLowerCase();
            const wallet = String(employee.wallet || '').toLowerCase();
            return name.includes(query) || wallet.includes(query);
        });
    }, [roster, statusFilter, typeFilter, searchQuery]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery, statusFilter, typeFilter]);

    const overviewStats = useMemo(() => {
        let active = 0;
        let inactive = 0;
        let salaried = 0;
        let freelance = 0;
        roster.forEach((e) => {
            const s = normalizeStatus(e.status);
            if (s === 'active') active += 1;
            else if (s === 'inactive') inactive += 1;
            if (normalizeEmploymentType(e.employmentType) === EMPLOYMENT_TYPE.SALARIED) {
                salaried += 1;
            } else {
                freelance += 1;
            }
        });
        return { total: roster.length, active, inactive, salaried, freelance };
    }, [roster]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * PAGE_SIZE;
    const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE);

    const handleFilterChange = (id) => {
        setStatusFilter(id);
        setPage(1);
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                    <h3 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white">
                        Employees
                    </h3>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">
                        Salaried payroll and freelance task workers.
                    </p>
                </div>
                {isCompanyOwner ? (
                    <button
                        type="button"
                        onClick={onAddEmployee}
                        disabled={!canAddEmployee || submitting}
                        className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-teal-600 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-teal-500 dark:hover:bg-teal-400"
                    >
                        <i className="fa-solid fa-plus text-[10px]" />
                        Add employee
                    </button>
                ) : null}
            </div>

            {isCompanyOwner ? (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <OverviewStatCard
                        label="Total roster"
                        value={overviewStats.total}
                        hint="On team (excl. removed)"
                        tone="teal"
                        selected={statusFilter === 'all'}
                        onClick={() => handleFilterChange('all')}
                    />
                    <OverviewStatCard
                        label="Active employees"
                        value={overviewStats.active}
                        hint="Can receive tasks & pay"
                        tone="emerald"
                        selected={statusFilter === 'active'}
                        onClick={() => handleFilterChange('active')}
                    />
                    <OverviewStatCard
                        label="Deactivated"
                        value={overviewStats.inactive}
                        hint="Inactive on roster"
                        tone="amber"
                        selected={statusFilter === 'inactive'}
                        onClick={() => handleFilterChange('inactive')}
                    />
                    <OverviewStatCard
                        label="Salaried"
                        value={overviewStats.salaried}
                        hint="Payroll employees"
                        tone="zinc"
                        selected={typeFilter === EMPLOYMENT_TYPE.SALARIED}
                        onClick={() => {
                            setTypeFilter(EMPLOYMENT_TYPE.SALARIED);
                            setPage(1);
                        }}
                    />
                </div>
            ) : null}

            <div className="flex flex-wrap gap-1 border-b border-zinc-200 dark:border-zinc-800">
                {TYPE_FILTERS.map((tab) => {
                    const selected = typeFilter === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => {
                                setTypeFilter(tab.id);
                                setPage(1);
                            }}
                            className={`relative px-4 py-2 text-sm font-medium transition ${
                                selected
                                    ? 'text-indigo-700 dark:text-indigo-400'
                                    : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200'
                            }`}
                        >
                            {tab.label}
                            {selected ? (
                                <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-indigo-600 dark:bg-indigo-400" />
                            ) : null}
                        </button>
                    );
                })}
            </div>

            <div className="relative">
                <i
                    className="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400"
                    aria-hidden
                />
                <input
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search by name or wallet address…"
                    className="w-full rounded-xl border border-zinc-200 bg-white py-2.5 pl-9 pr-3 text-sm text-zinc-900 outline-none transition-shadow placeholder:text-zinc-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-zinc-700 dark:bg-zinc-950/50 dark:text-zinc-100 dark:placeholder:text-zinc-500"
                    aria-label="Search employees"
                />
            </div>

            <div className="flex gap-1 border-b border-zinc-200 dark:border-zinc-800">
                {STATUS_FILTERS.map((tab) => {
                    const selected = statusFilter === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => handleFilterChange(tab.id)}
                            className={`relative px-4 py-2.5 text-sm font-medium transition ${
                                selected
                                    ? 'text-teal-700 dark:text-teal-400'
                                    : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200'
                            }`}
                        >
                            {tab.label}
                            {selected ? (
                                <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-teal-600 dark:bg-teal-400" />
                            ) : null}
                        </button>
                    );
                })}
            </div>

            {filtered.length ? (
                <div className="overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-zinc-200 bg-zinc-50/80 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-zinc-500">
                                    <th className="px-4 py-3 w-12">#</th>
                                    <th className="px-4 py-3">Name</th>
                                    <th className="px-4 py-3">Wallet address</th>
                                    <th className="px-4 py-3">Type</th>
                                    <th className="px-4 py-3">Compensation</th>
                                    <th className="px-4 py-3">Status</th>
                                    {isCompanyOwner ? (
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/80">
                                {pageRows.map((employee, index) => {
                                    const status = normalizeStatus(employee.status);
                                    const isActive = status === 'active';
                                    const rowBusy =
                                        submitting || statusUpdatingId === employee.id;
                                    const globalIndex = pageStart + index + 1;

                                    return (
                                        <tr
                                            key={employee.id}
                                            role={onViewEmployee ? 'button' : undefined}
                                            tabIndex={onViewEmployee ? 0 : undefined}
                                            onClick={() => onViewEmployee?.(employee)}
                                            onKeyDown={(e) => {
                                                if (
                                                    onViewEmployee &&
                                                    (e.key === 'Enter' || e.key === ' ')
                                                ) {
                                                    e.preventDefault();
                                                    onViewEmployee(employee);
                                                }
                                            }}
                                            className={`bg-white transition hover:bg-zinc-50/80 dark:bg-zinc-950/30 dark:hover:bg-zinc-900/40 ${
                                                onViewEmployee
                                                    ? 'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/40'
                                                    : ''
                                            }`}
                                        >
                                            <td className="px-4 py-3.5 tabular-nums text-zinc-500 dark:text-zinc-500">
                                                {String(globalIndex).padStart(2, '0')}
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <div className="flex min-w-0 items-center gap-3">
                                                    <WalletAvatar
                                                        address={employee.wallet}
                                                        imageUrl={employee.profileImageUrl}
                                                        size={36}
                                                    />
                                                    <span className="truncate font-medium text-zinc-900 dark:text-white">
                                                        {employee.name || 'Unnamed'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5">
                                                {employee.wallet ? (
                                                    <div className="inline-flex max-w-full items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 dark:border-zinc-700 dark:bg-zinc-900/60">
                                                        <span className="truncate font-mono text-xs font-medium text-teal-700 dark:text-teal-400">
                                                            {shortenWalletAddress(employee.wallet)}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                copyToClipboard(employee.wallet);
                                                            }}
                                                            className="shrink-0 text-zinc-400 transition hover:text-teal-600 dark:hover:text-teal-400"
                                                            title="Copy address"
                                                            aria-label="Copy wallet address"
                                                        >
                                                            <i className="fa-regular fa-copy text-xs" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="text-zinc-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <EmploymentTypePill type={employee.employmentType} />
                                            </td>
                                            <td className="px-4 py-3.5 tabular-nums">
                                                {formatCompensation(employee, tokenSymbol)}
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <StatusPill status={employee.status} />
                                            </td>
                                            {isCompanyOwner ? (
                                                <td className="px-4 py-3.5">
                                                    <div className="flex flex-wrap items-center justify-end gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onEditEmployee?.(employee);
                                                            }}
                                                            disabled={rowBusy}
                                                            className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 transition hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-zinc-600 dark:hover:bg-zinc-800"
                                                        >
                                                            Edit
                                                        </button>
                                                        {normalizeEmploymentType(employee.employmentType) ===
                                                        EMPLOYMENT_TYPE.SALARIED ? (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onFundPayroll?.(employee);
                                                                }}
                                                                disabled={
                                                                    rowBusy ||
                                                                    payrollFundingId === employee.id
                                                                }
                                                                className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-900 transition hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-200 dark:hover:bg-indigo-500/20"
                                                            >
                                                                {payrollFundingId === employee.id
                                                                    ? 'Funding…'
                                                                    : 'Fund payroll'}
                                                            </button>
                                                        ) : null}
                                                        {isActive ? (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onToggleStatus?.(employee);
                                                                }}
                                                                disabled={rowBusy}
                                                                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 transition hover:bg-amber-100 disabled:opacity-50 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200 dark:hover:bg-amber-500/20"
                                                            >
                                                                {statusUpdatingId === employee.id
                                                                    ? 'Updating…'
                                                                    : 'Deactivate'}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onToggleStatus?.(employee);
                                                                }}
                                                                disabled={rowBusy}
                                                                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200 dark:hover:bg-emerald-500/20"
                                                            >
                                                                {statusUpdatingId === employee.id
                                                                    ? 'Updating…'
                                                                    : 'Activate'}
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onRemoveEmployee?.(employee);
                                                            }}
                                                            disabled={rowBusy}
                                                            className="rounded-lg border border-transparent px-2 py-1.5 text-xs font-medium text-zinc-400 transition hover:text-rose-600 disabled:opacity-50 dark:hover:text-rose-400"
                                                            title="Remove from team"
                                                        >
                                                            <i className="fa-regular fa-trash-can" />
                                                        </button>
                                                    </div>
                                                </td>
                                            ) : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex flex-col gap-3 border-t border-zinc-200 bg-zinc-50/50 px-4 py-3 text-xs text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/30 dark:text-zinc-500 sm:flex-row sm:items-center sm:justify-between">
                        <p>
                            Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)}{' '}
                            of {filtered.length}
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={safePage <= 1}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 transition hover:border-zinc-300 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
                                aria-label="Previous page"
                            >
                                <i className="fa-solid fa-chevron-left text-[10px]" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={safePage >= totalPages}
                                className="flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 transition hover:border-zinc-300 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400"
                                aria-label="Next page"
                            >
                                <i className="fa-solid fa-chevron-right text-[10px]" />
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 py-14 text-center dark:border-zinc-800 dark:bg-zinc-900/40">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                        <i className="fa-regular fa-user text-lg text-zinc-400" />
                    </div>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        {searchQuery.trim()
                            ? 'No employees match your search. Try a different name or wallet.'
                            : statusFilter === 'all'
                              ? 'No employees added yet.'
                              : `No ${statusFilter} employees.`}
                    </p>
                    {isCompanyOwner && statusFilter === 'all' ? (
                        <button
                            type="button"
                            onClick={onAddEmployee}
                            disabled={!canAddEmployee || submitting}
                            className="mt-1 text-sm font-semibold text-teal-700 hover:text-teal-800 disabled:opacity-50 dark:text-teal-400"
                        >
                            Add your first employee
                        </button>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default EmployeesDirectory;
